/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMutation } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { type ConfluenceViewState } from '@confluence/comments-panel-queries';
import { useSessionData } from '@confluence/session-data';

import type {
	useCommentBulkReadMarkAllCommentsAsReadMutation as useCommentBulkReadMarkAllCommentsAsReadMutation$data,
	useCommentBulkReadMarkAllCommentsAsReadMutationVariables as useCommentBulkReadMarkAllCommentsAsReadMutation$variables,
} from './__types__/useCommentBulkReadMarkAllCommentsAsReadMutation';
import { useCommentBulkOperationsActions } from './useCommentBulkOperations';
import { useSyncReadComments } from './useSyncReadComments';
import type { Flags } from './useCommentBulkOperationsFlags';
import { useLocation } from './useLocation';

const useCommentBulkReadMarkAllCommentsAsReadMutation = gql`
	mutation useCommentBulkReadMarkAllCommentsAsReadMutation(
		$cloudId: ID!
		$contentId: String!
		$readView: ConfluenceViewState
	) {
		confluence_markAllCommentsAsRead(
			cloudId: $cloudId
			input: { contentId: $contentId, readView: $readView }
		) {
			success
			errors {
				message
			}
		}
	}
`;
export const useCommentBulkRead = (flags: Flags) => {
	const [contentId = ''] = usePageContentId();
	const { cloudId } = useSessionData();
	const location = useLocation();
	const { setIsBulkReadInProgress } = useCommentBulkOperationsActions();
	const syncReadComments = useSyncReadComments();

	const handleReadAllComments = () => {
		void readAllComments({
			variables: { contentId, cloudId, readView: location as ConfluenceViewState },
		});

		setIsBulkReadInProgress(true);
	};

	const [readAllComments] = useMutation<
		useCommentBulkReadMarkAllCommentsAsReadMutation$data,
		useCommentBulkReadMarkAllCommentsAsReadMutation$variables
	>(useCommentBulkReadMarkAllCommentsAsReadMutation, {
		onCompleted: async ({ confluence_markAllCommentsAsRead }) => {
			if (confluence_markAllCommentsAsRead?.success) {
				syncReadComments();
				flags.showSuccess();
			} else {
				flags.showFailure(handleReadAllComments);
			}
			setIsBulkReadInProgress(false);
		},
		onError: () => {
			flags.showFailure(handleReadAllComments);
			setIsBulkReadInProgress(false);
		},
	});
	return handleReadAllComments;
};
